// 1. Define a type for the user object (customize with your actual user data)
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { IRole, User } from '@/interfaces/Auth/auth.interface';
import { IOrder, IOrderResponse } from '@/interfaces/Order/order.interface';
import { ICheckoutMessageForm } from '@/pages/Settings/CheckoutMessage';
// import { IUser } from '@/interfaces/user.interface';

export type RolesState = Record<string, IRole>;
interface UserState {
  holidaySettings: ICheckoutMessageForm | null;
  isHolidayLoading: boolean;
  isAuthenticated: boolean;
  user: User | null;
  role: RolesState | null;
  isSideBarOpen: number;
  isActiveTab: string;
  userWebsiteId: string;
  loading: 'idle' | 'pending' | 'succeeded' | 'failed';
  userDetailsLoading: 'idle' | 'pending' | 'succeeded' | 'failed';
  isLoadingOrder: 'idle' | 'pending' | 'succeeded' | 'failed';
  error: string | null;
  notificationCount: number;
  orderList: IOrder[] | null;
  scrollPosition: number;
  collapsed: boolean
}

const initialState: UserState = {
  isAuthenticated: false,
  isHolidayLoading: false,
  user: null,
  isSideBarOpen: 0,
  isActiveTab: '',
  userWebsiteId: '',
  loading: 'idle',
  userDetailsLoading: 'idle',
  isLoadingOrder: 'idle',
  notificationCount: 0,
  error: null,
  role: null,
  orderList: null,
  holidaySettings: null,
  scrollPosition: 0,
  collapsed: false
};

export const fetchNotificationCount = createAsyncThunk(
  'user/fetchNotificationCount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `${API_URL}${API_ENDPOINT.notification}/count`,
        { method: 'GET' },
      );
      // console.log('fetchNotificationCount response', response);
      return response?.data?.count;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch notifications');
    }
  },
);

export const fetchLiveOrders = createAsyncThunk(
  'user/fetchLiveOrders',
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi<IOrderResponse>(
        `${API_URL}${API_ENDPOINT.orders}`,
        { method: 'GET' }
      );
      return response?.data?.order || [];
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch live orders');
    }
  }
);

export const fetchUserDetails = createAsyncThunk(
  'user/fetchUserDetails',
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi(`${API_URL}/user/logged-in`, {
        method: 'GET',
      });
      return response?.data || response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch user details');
    }
  },
);

export const fetchHolidaySettings = createAsyncThunk('user/fetchHol idaySettings', async (_, { rejectWithValue }) => {
  try {
    const response = await callApi(`${API_URL}${API_ENDPOINT.checkoutMessage}`,
      { method: 'GET' })

    return response?.data;
  }
  catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch holiday settings');
  }
})



const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },
    setUserData: (state, action: PayloadAction<User | null>) => {
      state.user = action.payload;
    },
    setUserWebsiteId: (state, action: PayloadAction<string>) => {
      state.userWebsiteId = action.payload;
    },
    setRole: (state, action: PayloadAction<RolesState>) => {
      state.role = action.payload;
    },
    setNotificationCount: (state, action: PayloadAction<number>) => {
      state.notificationCount = action.payload;
    },
    setSideBarOpen: (state, action: PayloadAction<number>) => {
      state.isSideBarOpen = action.payload;
    },
    setOrderList: (state, action: PayloadAction<IOrder[]>) => {
      state.orderList = action.payload;
      state.isLoadingOrder = 'succeeded';
    },
    setActiveTab: (state, action: PayloadAction<string>) => {
      state.isActiveTab = action.payload;
    },
    setCollapsed: (state, action: PayloadAction<boolean>) => {
      state.collapsed = action.payload;
    },
    logout: (state) => {
      Object.assign(state, initialState);
      state.isAuthenticated = false;
    },
    updateOrderStatus: (state, action: PayloadAction<{ orderId: string, status: string }>) => {
      if (state.orderList) {
        const index = state.orderList.findIndex(o => o.id === action.payload.orderId);
        if (index !== -1) {
          state.orderList[index].status = action.payload.status as any;
        }
      }
    },
    saveSideBarScrollPosition: (state, action: PayloadAction<number>) => {
      state.scrollPosition = action.payload;
    }
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificationCount.pending, (state) => {
        state.loading = 'pending';
        state.error = null;
      })
      .addCase(
        fetchNotificationCount.fulfilled,
        (state, action: PayloadAction<number>) => {
          state.loading = 'succeeded';
          state.notificationCount = action.payload;
        },
      )
      .addCase(fetchNotificationCount.rejected, (state, action) => {
        state.loading = 'failed';
        state.error = action.payload as string;
      })
      .addCase(fetchUserDetails.pending, (state) => {
        state.userDetailsLoading = 'pending';
      })
      .addCase(fetchUserDetails.fulfilled, (state, action: PayloadAction<User>) => {
        state.userDetailsLoading = 'succeeded';
        state.user = { ...state.user, ...action.payload };
      })
      .addCase(fetchUserDetails.rejected, (state, action) => {
        state.userDetailsLoading = 'failed';
        state.error = action.payload as string;
      })
      .addCase(fetchLiveOrders.pending, (state) => {
        state.isLoadingOrder = 'pending';
      })
      .addCase(fetchLiveOrders.fulfilled, (state, action: PayloadAction<IOrder[]>) => {
        state.isLoadingOrder = 'succeeded';
        state.orderList = action.payload;
      })
      .addCase(fetchLiveOrders.rejected, (state, action) => {
        state.isLoadingOrder = 'failed';
        state.error = action.payload as string;
      })
      .addCase(fetchHolidaySettings.pending, (state) => {
        state.isHolidayLoading = true;
      })
      .addCase(fetchHolidaySettings.fulfilled, (state, action) => {
        state.isHolidayLoading = false;
        state.holidaySettings = action.payload;
      })
      .addCase(fetchHolidaySettings.rejected, (state) => {
        state.isHolidayLoading = false;
      })
  },
});

export const {
  setAuthenticated,
  setUserData,
  setSideBarOpen,
  setActiveTab,
  logout,
  setUserWebsiteId,
  setRole,
  setNotificationCount,
  setOrderList,
  updateOrderStatus,
  saveSideBarScrollPosition,
  setCollapsed
} = userSlice.actions;

export default userSlice.reducer;
